import { supabase } from './supabaseClient';
import { GOOGLE_CLIENT_ID } from '../config/google';

const youtubeAuth = {
  /**
   * Initiates Google OAuth flow for YouTube access
   * Uses Authorization Code flow - requires backend exchange
   */
  signInWithGoogle() {
    const redirectUri = `${window.location.origin}/`;
    
    // YouTube-specific scopes needed for video uploads
    const scope = [
      'https://www.googleapis.com/auth/youtube.upload',
      'https://www.googleapis.com/auth/youtube.readonly',
      'https://www.googleapis.com/auth/userinfo.profile'
    ].join(' ');
    
    // State parameter for CSRF protection and to identify this OAuth flow
    const state = 'google_oauth_' + Date.now();
    sessionStorage.setItem('google_oauth_state', state);

    // Google OAuth uses Authorization Code flow (response_type=code)
    // The code will be exchanged for tokens on the backend
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('scope', scope);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('access_type', 'offline'); // Request refresh token
    authUrl.searchParams.set('prompt', 'consent'); // Always show consent to get refresh token

    if (!GOOGLE_CLIENT_ID) {
      alert('Google Client ID not configured. Please set GOOGLE_CLIENT_ID in src/config/google.js');
      return;
    }

    window.location.href = authUrl.toString();
  },

  /**
   * Handle the OAuth callback - extracts authorization code from URL
   * and exchanges it for tokens via the backend
   */
  async handleGoogleCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    // Check if this is a Google OAuth callback
    const savedState = sessionStorage.getItem('google_oauth_state');
    if (!state || !savedState || !state.startsWith('google_oauth_')) {
      return null; // Not a Google OAuth callback
    }

    // Verify state matches (CSRF protection)
    if (state !== savedState) {
      console.error('OAuth state mismatch - possible CSRF attack');
      return null;
    }

    if (error) {
      console.error('Google OAuth error:', error);
      sessionStorage.removeItem('google_oauth_state');
      return { error };
    }

    if (!code) {
      return null;
    }

    try {
      // Exchange authorization code for tokens via backend
      const backendUrl = 'https://toldfel.hu';
      
      const session = supabase.getSession();
      const userId = session?.user?.id || null;

      const response = await fetch(`${backendUrl}/api/google/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          code, 
          user_id: userId,
          redirect_uri: `${window.location.origin}/`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Token exchange failed');
      }

      const data = await response.json();
      
      // Store tokens in localStorage
      const tokenInfo = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
        channel_info: data.channel_info
      };
      localStorage.setItem('youtube_token', JSON.stringify(tokenInfo));
      
      // Clean up
      sessionStorage.removeItem('google_oauth_state');
      
      // Clean URL - remove code and state parameters
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('code');
        cleanUrl.searchParams.delete('state');
        cleanUrl.searchParams.delete('scope');
        window.history.replaceState({}, document.title, cleanUrl.pathname);
      } catch (e) {
        console.error('Failed to clean URL:', e);
      }

      return tokenInfo;
    } catch (e) {
      console.error('Failed to exchange Google auth code:', e);
      sessionStorage.removeItem('google_oauth_state');
      return { error: e.message };
    }
  },

  /**
   * Get stored YouTube token from localStorage
   */
  getToken() {
    const raw = localStorage.getItem('youtube_token');
    if (!raw) return null;
    try {
      const token = JSON.parse(raw);
      // Check if token is expired
      if (token.expires_at && new Date(token.expires_at) < new Date()) {
        // Token expired - need to refresh
        return { ...token, expired: true };
      }
      return token;
    } catch (e) {
      return null;
    }
  },

  /**
   * Load YouTube token from Supabase
   */
  async loadTokenFromSupabase() {
    try {
      const session = supabase.getSession();
      if (!session?.user?.id) return null;

      const tokens = await supabase.fetchPlatformTokens(session.user.id);
      const youtubeToken = tokens?.find(t => t.platform === 'youtube');
      
      if (youtubeToken?.access_token) {
        const tokenInfo = {
          access_token: youtubeToken.access_token,
          refresh_token: youtubeToken.refresh_token,
          expires_at: youtubeToken.expires_at,
          channel_info: youtubeToken.meta?.channel_info
        };
        localStorage.setItem('youtube_token', JSON.stringify(tokenInfo));
        return tokenInfo;
      }
      return null;
    } catch (e) {
      console.error('Failed to load YouTube token from Supabase:', e);
      return null;
    }
  },

  /**
   * Refresh the access token using the refresh token
   */
  async refreshAccessToken() {
    const token = this.getToken();
    if (!token?.refresh_token) {
      console.error('No refresh token available');
      return null;
    }

    try {
      const backendUrl = 'https://toldfel.hu';
      
      const session = supabase.getSession();
      const userId = session?.user?.id || null;

      const response = await fetch(`${backendUrl}/api/google/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          refresh_token: token.refresh_token,
          user_id: userId
        })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      // Update stored token
      const updatedToken = {
        ...token,
        access_token: data.access_token,
        expires_at: data.expires_at
      };
      localStorage.setItem('youtube_token', JSON.stringify(updatedToken));
      
      return updatedToken;
    } catch (e) {
      console.error('Failed to refresh YouTube token:', e);
      return null;
    }
  },

  /**
   * Get a valid access token, refreshing if necessary
   */
  async getValidAccessToken() {
    let token = this.getToken();
    
    if (!token?.access_token) {
      return null;
    }

    // If token is expired or about to expire (within 5 minutes), refresh it
    if (token.expires_at) {
      const expiresAt = new Date(token.expires_at);
      const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
      
      if (expiresAt < fiveMinutesFromNow && token.refresh_token) {
        token = await this.refreshAccessToken();
      }
    }

    return token?.access_token || null;
  },

  /**
   * Sign out - clear YouTube tokens from localStorage and Supabase
   */
  async signOut() {
    localStorage.removeItem('youtube_token');
    
    // Remove from Supabase
    try {
      const session = supabase.getSession();
      if (session && session.user && session.user.id) {
        await supabase.deletePlatformToken(session.user.id, 'youtube');
        console.log('YouTube token deleted from Supabase');
      }
    } catch (e) {
      console.error('Failed to delete YouTube token from Supabase:', e);
    }
  },

  /**
   * Check if connected to YouTube
   */
  isConnected() {
    const token = this.getToken();
    return !!(token?.access_token);
  }
};

export default youtubeAuth;
